/**
 * Derivaciones puras del módulo Adquisición.
 *
 * Vive aparte de los componentes porque es lo único de esta pantalla que se
 * puede testear sin DOM, y porque las tres reglas que más fácil se rompen son
 * aritméticas y no visuales:
 *
 *  1. Una etapa sin datapoint cargado vale `null`, **nunca 0**. El agregado del
 *     servidor (`funnelSummaryOf`) usa `?? 0` para armar su resumen, y un 0
 *     dibujado en una barra se lee como "ese mes no entró nadie". Acá se
 *     reconstruye desde `datapoints` para distinguir las dos cosas.
 *  2. Todo porcentaje viaja con su base. La conversión publica la etapa previa y
 *     su cantidad; el delta publica el período anterior y su cantidad.
 *  3. Un canal sin conversión medida no tiene semáforo: va "sin evaluar". No se
 *     le inventa un número ni un verde.
 */
import {
  growthMoMPct,
  type Channel,
  type DeltaBase,
  type FunnelDatapoint,
  type FunnelItem,
  type FunnelStage,
  type Health,
  type Prioridad,
  type Provenance,
  type SeoQuery,
} from '@ops/contracts'

/**
 * Etiquetas de `Prioridad`. El contrato exporta el enum pero no sus labels
 * (a diferencia de `CHANNEL_STATUS_LABELS` o `SEO_ACCION_LABELS`), así que
 * viven acá hasta que se agreguen allá.
 */
export const PRIORIDAD_LABEL: Record<Prioridad, string> = {
  alta: 'Alta',
  media: 'Media',
  baja: 'Baja',
}

/** Ancho de la barra de escalabilidad. La palabra siempre va al lado. */
export const ESCALABILIDAD_PCT: Record<Prioridad, number> = {
  alta: 100,
  media: 55,
  baja: 22,
}

/** Peso de orden: alta primero. */
const PESO_PRIORIDAD: Record<Prioridad, number> = { alta: 0, media: 1, baja: 2 }

// ---------------------------------------------------------------------------
// Funnel
// ---------------------------------------------------------------------------

export interface FilaEtapa {
  id: string
  nombre: string
  orden: number
  /** `null` = nadie cargó el agregado del mes. Nunca 0 por defecto. */
  cantidad: number | null
  /** Proveniencia del agregado. `null` cuando no hay dato que atribuir. */
  provenance: Provenance | null
  /** Conversión desde la etapa previa. `null` en la primera y sin dato. */
  conversionPct: number | null
  /** Base de la conversión: qué etapa y con cuántos. La regla 5 la exige. */
  baseConversion: { nombre: string; cantidad: number } | null
  /** Variación contra la misma etapa del mes anterior. */
  deltaPct: number | null
  /** Base del delta. Se publica incluso cuando su valor es null. */
  base: DeltaBase | null
  /** Ancho de la barra, relativo a la etapa más ancha con dato. */
  anchoPct: number
  health: Health
  /** La peor conversión del mes: el paso que frena la máquina. */
  esCuelloDeBotella: boolean
}

/** Agregado de una etapa: el datapoint sin canal. `null` si no se cargó. */
function agregadoDe(
  datapoints: readonly FunnelDatapoint[],
  stageId: string,
): FunnelDatapoint | null {
  return (
    datapoints.find((d) => d.canalId === null && d.stageId === stageId) ?? null
  )
}

export interface FilasEtapaInput {
  etapas: readonly FunnelStage[]
  datapoints: readonly FunnelDatapoint[]
  /** Datapoints del mes anterior. Vacío = no hay con qué comparar. */
  datapointsAnteriores: readonly FunnelDatapoint[]
  /** Período de la base del delta. `null` = no se publica base. */
  periodoBase: string | null
}

export function filasEtapa(input: FilasEtapaInput): FilaEtapa[] {
  const { etapas, datapoints, datapointsAnteriores, periodoBase } = input
  const ordenadas = [...etapas].sort((a, b) => a.orden - b.orden)

  const cantidades = ordenadas.map((etapa) => {
    const dp = agregadoDe(datapoints, etapa.id)
    return dp === null ? null : dp.cantidad
  })

  // El máximo y no la primera etapa: si la primera está sin cargar, la barra
  // igual tiene que tener una referencia con la que compararse.
  const maximo = cantidades.reduce<number>(
    (max, c) => (c !== null && c > max ? c : max),
    0,
  )

  const preliminar = ordenadas.map((etapa, i) => {
    const cantidad = cantidades[i] ?? null
    const anteriorEnFunnel = i === 0 ? null : (cantidades[i - 1] ?? null)
    const dpAnterior = agregadoDe(datapointsAnteriores, etapa.id)
    const cantidadMesAnterior = dpAnterior === null ? null : dpAnterior.cantidad

    const conversionPct =
      cantidad === null || anteriorEnFunnel === null || anteriorEnFunnel === 0
        ? null
        : (cantidad / anteriorEnFunnel) * 100

    const etapaPrevia = i === 0 ? null : ordenadas[i - 1]
    const baseConversion =
      conversionPct === null ||
      etapaPrevia === undefined ||
      etapaPrevia === null ||
      anteriorEnFunnel === null
        ? null
        : { nombre: etapaPrevia.nombre, cantidad: anteriorEnFunnel }

    const deltaPct =
      cantidad === null
        ? null
        : growthMoMPct({ actual: cantidad, anterior: cantidadMesAnterior })

    return {
      id: etapa.id,
      nombre: etapa.nombre,
      orden: etapa.orden,
      cantidad,
      provenance: agregadoDe(datapoints, etapa.id)?.provenance ?? null,
      conversionPct,
      baseConversion,
      deltaPct,
      base:
        periodoBase === null
          ? null
          : { periodo: periodoBase, valor: cantidadMesAnterior },
      anchoPct: cantidad === null || maximo === 0 ? 0 : (cantidad / maximo) * 100,
      esCuelloDeBotella: false,
      health: 'unknown' as Health,
    }
  })

  const cuello = peorConversion(preliminar)

  return preliminar.map((fila) => {
    const esCuelloDeBotella = cuello !== null && cuello.id === fila.id
    return {
      ...fila,
      esCuelloDeBotella,
      health:
        fila.cantidad === null
          ? 'unknown'
          : esCuelloDeBotella
            ? 'warning'
            : 'ok',
    }
  })
}

/** La etapa con la conversión más baja del mes. `null` si ninguna tiene. */
function peorConversion(
  filas: readonly { id: string; conversionPct: number | null }[],
): { id: string; conversionPct: number } | null {
  let peor: { id: string; conversionPct: number } | null = null
  for (const fila of filas) {
    if (fila.conversionPct === null) continue
    if (peor === null || fila.conversionPct < peor.conversionPct) {
      peor = { id: fila.id, conversionPct: fila.conversionPct }
    }
  }
  return peor
}

/** Etiqueta del estado de una etapa. El color nunca viaja solo. */
export function etiquetaEtapa(fila: FilaEtapa): string {
  if (fila.cantidad === null) return 'Sin cargar'
  if (fila.esCuelloDeBotella) return 'Cuello de botella'
  return 'Fluye'
}

// ---------------------------------------------------------------------------
// Canales
// ---------------------------------------------------------------------------

/** Un canal está evaluado cuando alguien midió su conversión. */
export function canalEvaluado(canal: Channel): boolean {
  return canal.conversionPct !== null
}

/**
 * Orden de lectura: primero lo que se puede decidir (evaluado, mejor conversión
 * arriba), después lo que falta evaluar, y al final lo que ya está fuera de
 * juego. Ordenar por conversión sola pondría los descartados en el medio.
 */
export function ordenarCanales(canales: readonly Channel[]): Channel[] {
  const grupo = (canal: Channel): number => {
    if (canal.status === 'descartado') return 3
    if (!canalEvaluado(canal)) return 2
    if (canal.status === 'pausado') return 1
    return 0
  }

  return [...canales].sort((a, b) => {
    const porGrupo = grupo(a) - grupo(b)
    if (porGrupo !== 0) return porGrupo
    const porConversion = (b.conversionPct ?? -1) - (a.conversionPct ?? -1)
    if (porConversion !== 0) return porConversion
    const porPrioridad = PESO_PRIORIDAD[a.prioridad] - PESO_PRIORIDAD[b.prioridad]
    if (porPrioridad !== 0) return porPrioridad
    return a.nombre.localeCompare(b.nombre, 'es')
  })
}

// ---------------------------------------------------------------------------
// SEO
// ---------------------------------------------------------------------------

/**
 * El semáforo de una query es **la página en la que cae el resultado**, no un
 * umbral inventado: hasta la posición 10 es primera página, hasta 20 segunda, y
 * más abajo nadie la ve. La regla se publica en la bajada de la sección para que
 * el color sea auditable.
 */
export const SEO_PRIMERA_PAGINA = 10
export const SEO_SEGUNDA_PAGINA = 20

export function severidadSeo(query: SeoQuery): Health {
  const { posicion } = query
  if (posicion === null) return 'unknown'
  if (posicion <= SEO_PRIMERA_PAGINA) return 'ok'
  if (posicion <= SEO_SEGUNDA_PAGINA) return 'warning'
  return 'critical'
}

export function etiquetaSeo(query: SeoQuery): string {
  const { posicion } = query
  if (posicion === null) return 'Sin medición'
  if (posicion <= SEO_PRIMERA_PAGINA) return 'Primera página'
  if (posicion <= SEO_SEGUNDA_PAGINA) return 'Segunda página'
  return 'Fuera de las dos primeras'
}

// ---------------------------------------------------------------------------
// Triage de la pantalla
// ---------------------------------------------------------------------------

export interface ConteosAdquisicion {
  /** Etapas del funnel sin agregado cargado: el funnel no se puede leer. */
  etapasSinCargar: number
  /** Canales sin conversión medida. */
  canalesSinEvaluar: number
  /** Queries sin dato de rendimiento de Search Console. */
  queriesSinMedicion: number
  /** Oportunidades que no tienen lead en Close: se trabajan fuera del CRM. */
  oportunidadesFueraDelCrm: number
  /** Cargas con más de 30 días: la regla de frescura las marca vencidas. */
  cargasVencidas: number
}

export interface ConteosInput {
  filas: readonly FilaEtapa[]
  canales: readonly Channel[]
  seoQueries: readonly SeoQuery[]
  oportunidades: readonly FunnelItem[]
}

export function conteosAdquisicion(input: ConteosInput): ConteosAdquisicion {
  const { filas, canales, seoQueries, oportunidades } = input

  const vencidas = [
    ...filas.map((f) => f.provenance),
    ...canales.map((c) => c.provenance),
    ...seoQueries.map((q) => q.provenance),
  ].filter((p): p is Provenance => p !== null && p.freshness === 'expired')

  return {
    etapasSinCargar: filas.filter((f) => f.cantidad === null).length,
    canalesSinEvaluar: canales.filter((c) => !canalEvaluado(c)).length,
    queriesSinMedicion: seoQueries.filter((q) => q.posicion === null).length,
    oportunidadesFueraDelCrm: oportunidades.filter(
      (o) => o.closeLeadId === null,
    ).length,
    cargasVencidas: vencidas.length,
  }
}

export interface TitularAdquisicion {
  estado: Health
  titular: string
}

/**
 * Veredicto de la pantalla, en palabras. Nunca "inventa" un estado: si no hay
 * nada cargado lo dice, y si el funnel se lee entero el titular habla del cuello
 * de botella, que es lo único accionable que queda.
 */
export function titularAdquisicion(
  filas: readonly FilaEtapa[],
  conteos: ConteosAdquisicion,
): TitularAdquisicion {
  if (filas.length === 0) {
    return {
      estado: 'unknown',
      titular: 'Adquisición sin nada cargado',
    }
  }

  if (conteos.etapasSinCargar > 0) {
    const plural = conteos.etapasSinCargar === 1 ? 'etapa' : 'etapas'
    return {
      estado: 'critical',
      titular: `El funnel no se lee: ${String(conteos.etapasSinCargar)} ${plural} sin cargar`,
    }
  }

  if (conteos.cargasVencidas > 0) {
    const una = conteos.cargasVencidas === 1
    return {
      estado: 'warning',
      titular: `${String(conteos.cargasVencidas)} ${una ? 'carga vencida' : 'cargas vencidas'} hace más de 30 días`,
    }
  }

  const cuello = filas.find((f) => f.esCuelloDeBotella)
  if (cuello !== undefined) {
    return {
      estado: 'warning',
      titular: `El paso más angosto es ${cuello.nombre}`,
    }
  }

  return { estado: 'ok', titular: 'El funnel se lee entero' }
}
